/**
 * Helpers that derive pre-generated thumbnail URLs from the original
 * Supabase Storage public URLs.
 *
 * Why this exists: Vercel's free tier caps image transformations at 5,000 per
 * month, and the project does not pay for Supabase Pro (where the
 * `/storage/v1/render/image/public/` endpoint would resize on demand). To
 * avoid both costs, the Trigger.dev image-generation tasks pre-generate two
 * sizes per source image and upload them as separate files. The app then
 * references the right pre-sized file directly and runs `next/image` with
 * `images.unoptimized: true` so the browser fetches the file as-is.
 *
 * Pre-generated variants (uploaded alongside the original):
 *   - Recipe hero      → `{recipeId}/{variantType}-hero-card.webp`  (400×400)
 *   - Ingredient image → `{ingredientId}-thumb.webp`                 (128×128)
 *
 * Existing images uploaded before this pipeline existed don't have the
 * variant file in Storage yet. Components render through `<ThumbImage>` which
 * falls back to the original URL on a 404. After running
 * `scripts/backfill-image-thumbnails.ts`, every existing image has its
 * variant and the fallback path becomes unreachable.
 *
 * Both helpers preserve the `?v={timestamp}` cache-buster that the image
 * generation tasks attach so regenerated images bust browser caches.
 */

/**
 * Derive the 400×400 card variant URL from a recipe hero URL.
 * Input shape: `…/{recipeId}/{variantType}-hero.webp[?v=…]`
 * Output:      `…/{recipeId}/{variantType}-hero-card.webp[?v=…]`
 */
export function getRecipeCardImageUrl(
  heroUrl: string | null | undefined,
): string | null {
  if (!heroUrl) return null;
  return insertSuffixBeforeExtension(heroUrl, '-card');
}

/**
 * Derive the 128×128 thumbnail URL from an ingredient image URL.
 * Input shape: `…/{ingredientId}.webp[?v=…]`
 * Output:      `…/{ingredientId}-thumb.webp[?v=…]`
 */
export function getIngredientThumbImageUrl(
  originalUrl: string | null | undefined,
): string | null {
  if (!originalUrl) return null;
  return insertSuffixBeforeExtension(originalUrl, '-thumb');
}

function insertSuffixBeforeExtension(url: string, suffix: string): string {
  // Defensive: if URL parsing fails (e.g. someone passes a non-URL string)
  // fall back to the original — better than throwing in a render path.
  try {
    const u = new URL(url);
    u.pathname = u.pathname.replace(/\.(webp|jpe?g|png)$/i, `${suffix}.$1`);
    return u.toString();
  } catch {
    return url;
  }
}
